import { z } from "zod";
import { getMoroccoNow } from "../bookings/booking-time";

/**
 * House rules a restaurant enforces on a table reservation.
 *
 * These live per venue rather than in code because they genuinely differ per
 * restaurant. Dar Soukkar sends a payment link above 7 people and only admits
 * children Monday to Thursday; the next restaurant we make bookable will have
 * its own set, or none at all.
 *
 * The booking form reads these to warn the guest *before* they submit. It never
 * blocks the reservation on them — our team confirms every table with the
 * restaurant anyway, and a wrong guess here would cost a booking.
 */
export const tablePolicySchema = z.object({
  /**
   * Party size above which the restaurant takes payment up front, by a link it
   * sends itself. Above this the form stops promising "free to reserve, no
   * card". Omit when the venue never asks for one.
   */
  paymentLinkOverGuests: z.number().int().min(1).max(50).optional(),
  /**
   * Deposit per person, in MAD, the restaurant asks for once the party is over
   * `paymentLinkOverGuests`. Dar Soukkar asks 300 MAD a head above 6. Omit when
   * the venue sends a link without a fixed amount; the form then only says a
   * link is coming.
   */
  depositPerGuest: z.number().int().min(1).max(100000).optional(),
  /**
   * Days children are admitted, ISO weekday numbers (1 = Monday … 7 = Sunday).
   * Omit when children are welcome every day.
   */
  childrenDays: z.array(z.number().int().min(1).max(7)).max(7).optional(),
  /**
   * Minimum age for a child on days outside `childrenDays`. Dar Soukkar admits
   * children Monday to Thursday, and refuses under-6s on Friday to Sunday —
   * that is this field, not a blanket ban.
   */
  childrenMinAgeOtherDays: z.number().int().min(0).max(17).optional(),
  /**
   * Online booking for the same night closes at `time` (Morocco clock) on
   * these ISO weekdays. L'Octave Casablanca wants Friday and Saturday tables
   * in by 17:00 so it can plan the night; after that, tonight is no longer
   * offered and the guest is pointed at another day or WhatsApp. Other nights
   * stay bookable as usual. Unlike the rules above, this one is enforced: the
   * API refuses a same-night booking past the cutoff.
   */
  sameDayCutoff: z
    .object({
      days: z.array(z.number().int().min(1).max(7)).min(1).max(7),
      time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    })
    .optional(),
  /**
   * Minimum the restaurant expects each guest to spend on the night, in MAD.
   * Folk asks 350 MAD a head; two guests who spent 300 MAD between them were
   * a complaint. The form states it before the guest books. Nothing is
   * charged by us, so it is a warning, never a price.
   */
  minSpendPerGuest: z.number().int().min(1).max(100000).optional(),
  /**
   * Nights the minimum spend applies, ISO weekday numbers (1 = Monday … 7 =
   * Sunday). Omit when it applies every night. Ruby Supper Club asks 850 MAD a
   * head on Friday, Saturday and Sunday only (owner, 2026-10-31).
   */
  minSpendDays: z.array(z.number().int().min(1).max(7)).min(1).max(7).optional(),
  /**
   * Seatings that must hand the table back by a set time. Folk runs two
   * services, 18:30 and 21:30, and the 18:30 table has to leave at 21:30 so
   * the second one can be laid.
   */
  seatingEnds: z
    .array(
      z.object({
        time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
        endsAt: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
      }),
    )
    .max(48)
    .optional(),
  /**
   * How long before closing the last table is taken, in minutes. Omit for the
   * house default of an hour (`LAST_SEATING_BEFORE_CLOSE_MIN`). Le Grand Bazar
   * closes at midnight and seats until 23:30 on its own booking page, so it is
   * 30 (owner, 2026-10-24). 0 seats right up to the close.
   */
  lastSeatingBeforeCloseMin: z.number().int().min(0).max(180).optional(),
});

export type TablePolicy = z.infer<typeof tablePolicySchema>;

/** ISO weekday (1 = Monday … 7 = Sunday) for a "YYYY-MM-DD" date. */
export function isoWeekday(iso: string): number | null {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  const day = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return day === 0 ? 7 : day;
}

/**
 * Whether the venue restricts children on this date. Returns the minimum age
 * that still applies, or null when children are unrestricted that day.
 *
 * `undefined` policy, no `childrenDays`, or a date inside `childrenDays` all
 * mean no restriction.
 */
export function childrenRestrictionFor(
  policy: TablePolicy | null | undefined,
  iso: string,
): { minAge: number } | null {
  if (!policy?.childrenDays || policy.childrenDays.length === 0) return null;
  const weekday = isoWeekday(iso);
  if (weekday === null) return null;
  if (policy.childrenDays.includes(weekday)) return null;
  return { minAge: policy.childrenMinAgeOtherDays ?? 0 };
}

/** Whether this party size triggers the restaurant's own payment link. */
export function needsPaymentLink(
  policy: TablePolicy | null | undefined,
  guests: number,
): boolean {
  const over = policy?.paymentLinkOverGuests;
  return typeof over === "number" && guests > over;
}

/**
 * The deposit this party size owes the restaurant, in MAD: per-person amount
 * and the party total. Null when no payment link applies or the venue has no
 * fixed amount.
 */
export function tableDepositFor(
  policy: TablePolicy | null | undefined,
  guests: number,
): { perGuest: number; total: number } | null {
  const perGuest = policy?.depositPerGuest;
  if (typeof perGuest !== "number" || !needsPaymentLink(policy, guests)) return null;
  return { perGuest, total: perGuest * guests };
}

/**
 * Whether online booking for the night of `date` has closed under the venue's
 * `sameDayCutoff`. Returns the cutoff time ("17:00") when it has, else null.
 *
 * Only tonight is ever closed: `date` is the night the guest picked, and it
 * must be today in Morocco, on one of the listed weekdays, at or after the
 * cutoff minute.
 */
export function sameDayCutoffPassed(
  policy: TablePolicy | null | undefined,
  date: string | null | undefined,
  now: Date = new Date(),
): string | null {
  const cutoff = policy?.sameDayCutoff;
  if (!cutoff || !date) return null;
  const moroccoNow = getMoroccoNow(now);
  if (date !== moroccoNow.date) return null;
  const weekday = isoWeekday(date);
  if (weekday === null || !cutoff.days.includes(weekday)) return null;
  return moroccoNow.time >= cutoff.time ? cutoff.time : null;
}

/**
 * The restaurant's minimum spend for this party, in MAD: per-person amount and
 * the party total. Null when the venue sets none, or when it applies only on
 * `minSpendDays` and `date` is not one of them (or not picked yet).
 */
export function tableMinSpendFor(
  policy: TablePolicy | null | undefined,
  guests: number,
  date?: string | null,
): { perGuest: number; total: number } | null {
  const perGuest = policy?.minSpendPerGuest;
  if (typeof perGuest !== "number" || perGuest <= 0) return null;
  const days = policy?.minSpendDays;
  if (days && days.length > 0) {
    const weekday = date ? isoWeekday(date) : null;
    if (weekday === null || !days.includes(weekday)) return null;
  }
  return { perGuest, total: perGuest * Math.max(1, Math.floor(guests) || 1) };
}

/** When a table booked at `time` must be given back ("21:30"), else null. */
export function seatingEndFor(
  policy: TablePolicy | null | undefined,
  time: string | null | undefined,
): string | null {
  if (!time) return null;
  return policy?.seatingEnds?.find((s) => s.time === time)?.endsAt ?? null;
}
